import { module as Module } from 'modujs';
import { $hasMenuOpen } from '../stores/state';
import { createFocusTrap } from 'focus-trap';
import { delegateEvent } from '../utils/events';

export default class Menu extends Module {
    static ACTIVE_CLASS = 'has-menu-open';

    static SELECTOR = '[data-menu-open]';

    constructor(m) {
        super(m);

        // Binding
        this.onActivate = this.onActivate.bind(this);
        this.onPostActivate = this.onPostActivate.bind(this);
        this.onDeactivate = this.onDeactivate.bind(this);
        this.onPostDeactivate = this.onPostDeactivate.bind(this);
        this.onMenuOpenChange = this.onMenuOpenChange.bind(this);
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);

        // UI
        this.$close = this.$('close')[0];
        this.$container = this.$('container')[0];
        this.$head = this.$('head')[0];

        // Focus trap
        this.focusTrap = createFocusTrap([this.$head, this.$container, '.c-header.-is-fixed'], {
            onActivate: this.onActivate,
            onPostActivate: this.onPostActivate,
            onDeactivate: this.onDeactivate,
            onPostDeactivate: this.onPostDeactivate,
            clickOutsideDeactivates: true
        });
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================
    init() {
        this.bindEvents();
    }

    destroy() {
        super.destroy();
        this.unbindEvents();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        this.unbindMenuOpenListener = $hasMenuOpen.listen(this.onMenuOpenChange);
        this.$close?.addEventListener('click', this.onClose);
        this.openEvent = delegateEvent(document.body, 'click', Menu.SELECTOR, this.onOpen);
    }

    unbindEvents() {
        this.unbindMenuOpenListener?.();
        this.$close?.removeEventListener('click', this.onClose);
        this.openEvent.abort?.();
    }

    // =============================================================================
    // Callbacks
    // =============================================================================
    onActivate() {
        this.el.setAttribute('aria-hidden', false);
    }

    onPostActivate() {
        this.$triggerCta?.setAttribute('aria-expanded', true);
    }

    onDeactivate() {
        $hasMenuOpen.set(false);
        this.el.setAttribute('aria-hidden', true);
    }

    onPostDeactivate() {
        this.$triggerCta?.setAttribute('aria-expanded', false);
    }

    onMenuOpenChange(hasMenuOpen) {
        if (hasMenuOpen) {
            document.documentElement.classList.add(Menu.ACTIVE_CLASS);
        } else {
            document.documentElement.classList.remove(Menu.ACTIVE_CLASS);
        }
    }

    onOpen(e) {
        this.$triggerCta = e?.target;
        this.open();
    }

    onClose() {
        this.close();
    }

    // =============================================================================
    // Methods
    // =============================================================================
    open() {
        if ($hasMenuOpen.get()) return;

        this.$container?.scrollTo(0, 0);

        $hasMenuOpen.set(true);

        requestAnimationFrame(() => {
            this.focusTrap?.activate?.();
        });
    }

    close() {
        if (!$hasMenuOpen.get()) return;
        this.$triggerCta = null;
        this.focusTrap?.deactivate?.();
    }
}
